<template>
  <v-dialog
    v-model="modal"
    max-width="720px"
    persistent>
    <v-card>
      <v-card-title>
        <span class="headline">
          <span class="text-error">VOID</span> | {{ orderDetail.orderId }}
        </span>
      </v-card-title>
      <v-card-subtitle class="pt-2">
        เลือกจำนวนสินค้าที่ลูกค้าคืน (เฉพาะรายการที่จัดส่งแล้ว) — ส่วนที่ไม่คืนจะถือว่าลูกค้าเก็บไว้
      </v-card-subtitle>
      <v-card-text>
        <v-simple-table class="return-item-table">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">
                  Items
                </th>
                <th class="text-left">
                  สาขา SUB ORDER
                </th>
                <th class="text-center">
                  สถานะ
                </th>
                <th
                  class="text-center"
                  style="width: 140px">
                  จำนวนที่ลูกค้าคืน
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.key">
                <td>
                  <div class="font-weight-medium">
                    {{ row.name }}
                  </div>
                  <div
                    v-if="row.variant"
                    class="text-caption grey--text">
                    {{ row.variant }}
                  </div>
                </td>
                <td>
                  <div>
                    {{ row.warehouseName }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ row.subOrderId ? `#${row.subOrderId}` : '-' }}
                  </div>
                </td>
                <td class="text-center">
                  <span :class="row.shipped ? 'success--text' : 'grey--text'">
                    {{ row.shipped ? 'จัดส่งแล้ว' : 'ยังไม่จัดส่ง' }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="d-flex justify-center">
                    <v-select
                      v-if="row.shipped"
                      v-model="row.returnQty"
                      :items="qtyOptions(row)"
                      :suffix="`/ ${row.amount}`"
                      class="return-qty-select"
                      dense
                      outlined
                      hide-details />
                    <p
                      v-else
                      class="mb-0">
                      -
                    </p>
                  </div>
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td
                  colspan="4"
                  class="text-center grey--text py-6">
                  ไม่พบรายการสินค้า
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="red darken-1"
          text
          @click="onCancel()">
          ปิดหน้าต่าง
        </v-btn>
        <v-btn
          color="blue darken-1"
          text
          @click="onConfirm()">
          ยืนยัน
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      rows: []
    }
  },
  computed: {
    ...mapGetters({
      orderDetail: 'Order/orderDetail'
    }),
    modal: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value (val) {
      if (val) {
        this.buildRows()
      }
    }
  },
  methods: {
    buildRows () {
      const rows = []
      const subOrders = this.orderDetail?.subOrders || []

      if (subOrders.length) {
        subOrders.forEach((subOrder, subIndex) => {
          const shipped = subOrder?.currentState === 'completed'
          const warehouse = subOrder?.warehouse || {}
          const subOrderId = subOrder?.orderId || null
          const skus = subOrder?.skus || []

          skus.forEach((sku, skuIndex) => {
            rows.push(this.mapRow(sku, warehouse, shipped, `${subIndex}-${skuIndex}`, subOrderId))
          })
        })
      } else {
        // fallback: order without sub-orders — infer shipping from order-level state
        const shipped = this.orderDetail?.currentState === 'completed'
        const skus = this.orderDetail?.skus || []

        skus.forEach((sku, skuIndex) => {
          rows.push(this.mapRow(sku, sku.warehouse || {}, shipped, `sku-${skuIndex}`))
        })
      }

      this.rows = rows
    },
    mapRow (sku, warehouse, shipped, index, subOrderId = null) {
      return {
        key: `${warehouse.id}-${sku.id}-${index}`,
        skuId: sku.id,
        productId: sku.productId,
        name: sku.name || sku.code || '-',
        variant: this.buildVariant(sku),
        amount: sku.amount || 0,
        warehouseId: warehouse.id,
        warehouseName: warehouse.name || '-',
        subOrderId,
        shipped,
        returnQty: shipped ? (sku.amount || 0) : 0
      }
    },
    qtyOptions (row) {
      return Array.from({ length: row.amount + 1 }, (_, qty) => qty)
    },
    buildVariant (sku) {
      const parts = [sku.code, sku.color, sku.size].filter(Boolean)
      let text = parts.join(' · ')
      if (sku.amount) {
        text = text ? `${text} · x${sku.amount}` : `x${sku.amount}`
      }
      return text
    },
    toItem (row, amount, refundCase) {
      return {
        productId: row.productId,
        skuId: row.skuId,
        warehouseId: row.warehouseId,
        subOrderId: row.subOrderId,
        amount,
        refundCase
      }
    },
    toItems (row) {
      // ต้องส่งครบทุกชิ้น: ส่งแล้ว = คืน (returnQty) + ไม่คืน (ที่เหลือ), ยังไม่ส่ง = NOT_SHIPPED ทั้งหมด
      if (!row.shipped) {
        return [this.toItem(row, row.amount, 'NOT_SHIPPED')]
      }
      const notReturnedQty = row.amount - row.returnQty
      return [
        row.returnQty > 0 ? this.toItem(row, row.returnQty, 'RETURNED') : null,
        notReturnedQty > 0 ? this.toItem(row, notReturnedQty, 'NOT_RETURNED') : null
      ].filter(Boolean)
    },
    onConfirm () {
      const items = this.rows.flatMap((row) => this.toItems(row))

      this.$emit('confirm', items)
      this.modal = false
    },
    onCancel () {
      this.modal = false
    }
  }
}
</script>

<style lang="scss" scoped>
.return-item-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;

  ::v-deep table {
    th,
    td {
      border-right: 1px solid rgba(0, 0, 0, 0.12);
    }
    th:last-child,
    td:last-child {
      border-right: none;
    }
    th {
      font-size: 0.8rem;
    }
  }
}

.return-qty-select {
  max-width: 120px;
}
</style>
